import {
  useCallback,
  useEffect,
  useState,
} from 'react';

export type FinancialCategory = {
  id: string;
  name: string;
  categoryType: string;
  isSystem: boolean;
  isActive: boolean;
  sortOrder: number;
};

export type FinancialLedgerEntry = {
  id: string;
  accountId: string;
  accountName: string | null;
  amountCents: number;
  entryRole: string;
};

export type FinancialTransaction = {
  id: string;
  transactionType: string;
  amountCents: number;

  categoryId: string | null;
  categoryName: string | null;

  clientId: string | null;
  clientName: string | null;

  caseId: string | null;
  caseTitle: string | null;

  occurredAt: string;
  description: string;

  reference: string | null;
  notes: string | null;

  source: string;

  externalReference:
    string | null;

  idempotencyKey:
    string | null;

  reversalOfId:
    string | null;

  reversedById:
    string | null;

  createdByUserId:
    string | null;

  createdByName:
    string | null;

  createdAt: string;

  entries:
    FinancialLedgerEntry[];
};

export type FinancialTransactionFilters = {
  type?: string;
  accountId?: string;
  clientId?: string;
  caseId?: string;
  from?: string;
  to?: string;
  limit?: number;
};

export type CreateFinancialTransactionInput = {
  transactionType:
    | 'income'
    | 'expense'
    | 'transfer';

  amountCents: number;

  accountId: string;

  destinationAccountId?:
    string | null;

  categoryId?:
    string | null;

  clientId?:
    string | null;

  caseId?:
    string | null;

  occurredAt: string;
  description: string;

  reference?:
    string | null;

  notes?:
    string | null;

  idempotencyKey: string;
};

export function useFinancialLedger() {
  const [
    financialCategories,
    setFinancialCategories,
  ] = useState<
    FinancialCategory[]
  >([]);

  const [
    financialTransactions,
    setFinancialTransactions,
  ] = useState<
    FinancialTransaction[]
  >([]);

  const [
    isLoadingFinancialCategories,
    setIsLoadingFinancialCategories,
  ] = useState(false);

  const [
    isLoadingFinancialTransactions,
    setIsLoadingFinancialTransactions,
  ] = useState(false);

  const [
    financialLedgerError,
    setFinancialLedgerError,
  ] = useState<string | null>(
    null
  );

  const loadFinancialCategories =
    useCallback(async () => {
      setIsLoadingFinancialCategories(
        true
      );

      try {
        const response =
          await fetch(
            '/api/financial-categories',
            {
              credentials:
                'include',
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !Array.isArray(
            data.categories
          )
        ) {
          throw new Error(
            data.error ||
              `GET categorias financeiras falhou com status ${response.status}`
          );
        }

        setFinancialCategories(
          data.categories
        );

        return data.categories as
          FinancialCategory[];
      } catch (error: any) {
        setFinancialCategories(
          []
        );

        setFinancialLedgerError(
          error?.message ||
            'Não foi possível carregar as categorias financeiras.'
        );

        return [];
      } finally {
        setIsLoadingFinancialCategories(
          false
        );
      }
    }, []);

  const loadFinancialTransactions =
    useCallback(
      async (
        filters:
          FinancialTransactionFilters = {}
      ) => {
        setIsLoadingFinancialTransactions(
          true
        );

        setFinancialLedgerError(
          null
        );

        try {
          const params =
            new URLSearchParams();

          if (filters.type) {
            params.set(
              'type',
              filters.type
            );
          }

          if (
            filters.accountId
          ) {
            params.set(
              'accountId',
              filters.accountId
            );
          }

          if (
            filters.clientId
          ) {
            params.set(
              'clientId',
              filters.clientId
            );
          }

          if (filters.caseId) {
            params.set(
              'caseId',
              filters.caseId
            );
          }

          if (filters.from) {
            params.set(
              'from',
              filters.from
            );
          }

          if (filters.to) {
            params.set(
              'to',
              filters.to
            );
          }

          params.set(
            'limit',
            String(
              filters.limit ?? 200
            )
          );

          const response =
            await fetch(
              `/api/financial-transactions?${params.toString()}`,
              {
                credentials:
                  'include',
              }
            );

          const data =
            await response.json();

          if (
            !response.ok ||
            !data.success ||
            !Array.isArray(
              data.transactions
            )
          ) {
            throw new Error(
              data.error ||
                `GET movimentações financeiras falhou com status ${response.status}`
            );
          }

          setFinancialTransactions(
            data.transactions
          );

          return (
            data.transactions as
              FinancialTransaction[]
          );
        } catch (error: any) {
          setFinancialTransactions(
            []
          );

          setFinancialLedgerError(
            error?.message ||
              'Não foi possível carregar o livro caixa.'
          );

          return [];
        } finally {
          setIsLoadingFinancialTransactions(
            false
          );
        }
      },
      []
    );

  const createFinancialTransaction =
    useCallback(
      async (
        input:
          CreateFinancialTransactionInput
      ) => {
        const response =
          await fetch(
            '/api/financial-transactions',
            {
              method: 'POST',
              credentials:
                'include',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.transaction
        ) {
          throw new Error(
            data.error ||
              `POST movimentação financeira falhou com status ${response.status}`
          );
        }

        return {
          transaction:
            data.transaction as
              FinancialTransaction,

          duplicated:
            Boolean(
              data.duplicated
            ),
        };
      },
      []
    );

  const reverseFinancialTransaction =
    useCallback(
      async (
        transactionId: string,
        input: {
          reason: string;
          occurredAt?: string;
        }
      ) => {
        const response =
          await fetch(
            `/api/financial-transactions/${encodeURIComponent(transactionId)}/reverse`,
            {
              method: 'POST',
              credentials:
                'include',

              headers: {
                'Content-Type':
                  'application/json',
              },

              body:
                JSON.stringify(
                  input
                ),
            }
          );

        const data =
          await response.json();

        if (
          !response.ok ||
          !data.success ||
          !data.reversal
        ) {
          throw new Error(
            data.error ||
              `POST estorno financeiro falhou com status ${response.status}`
          );
        }

        return data;
      },
      []
    );

  useEffect(() => {
    void loadFinancialCategories();
  }, [
    loadFinancialCategories,
  ]);

  return {
    financialCategories,
    financialTransactions,

    isLoadingFinancialCategories,
    isLoadingFinancialTransactions,

    financialLedgerError,

    loadFinancialCategories,
    loadFinancialTransactions,
    createFinancialTransaction,
    reverseFinancialTransaction,
  };
}